"use client";

import type { ReactNode, Dispatch, SetStateAction } from "react";
import { createContext, useContext, useState, useCallback } from "react";
import { AiFilterItemType } from "../components/AiFilters/types";

interface IAiFilterProviderProps {
  children: ReactNode;
}

type AiFilterContextType = {
  selectedItems: string[];
  selectItem: (id: string) => void;
  selectItems: (ids: string[]) => void;
  toggleAll: (ids: AiFilterItemType[]) => void;
  isSelected: (id: string) => boolean;
  clearSelectedAiFilter: VoidFunction;
  pageSizeState: number;
  setPageSizeState: (pageSize: number) => void;
  manageSelectedTab: (filterName: string, items: string[]) => void;
  selectedTab: string;
  setSelectedTab: Dispatch<SetStateAction<string>>;
};

const AiFilterContext = createContext({} as AiFilterContextType);
export function useAiFilterContext() {
  return useContext(AiFilterContext);
}

export function AiFilterProvider({ children }: IAiFilterProviderProps) {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [pageSizeState, setPageSizeState] = useState<number>(1);
  // Default to "filters" (selectable) tab when no items are selected
  const [selectedTab, setSelectedTab] = useState<string>("filters");

  const isSelected = useCallback(
    (id: string) => {
      if (id) {
        return Boolean(selectedItems.find((item: string) => item === id));
      }
      return false;
    },
    [selectedItems]
  );

  function selectItem(id: string) {
    if (isSelected(id)) {
      setSelectedItems((prevItems) => {
        return prevItems.filter((item: string) => item !== id);
      });
    } else {
      setSelectedItems((prevItems) => [...prevItems, id]);
    }
  }

  function selectItems(ids: string[]) {
    setSelectedItems(ids);
  }

  function toggleAll(items: AiFilterItemType[]): void {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      const list = items.map((filterItem) => filterItem.name);
      setSelectedItems(list);
    }
  }

  function clearSelectedAiFilter() {
    setSelectedItems([]);
  }

  const manageSelectedTab = useCallback(
    (filterName: string, items: string[]) => {
      // If no items selected, always show selectable filters tab
      if (items.length === 0) {
        setSelectedTab("filters");
        return;
      }
      
      if (filterName && items.length > 0) {
        const isIncludes = Boolean(
          items.find((item: string) =>
            item.toLowerCase()?.includes(filterName.toLowerCase())
          )
        );
        if (isIncludes) {
          setSelectedTab("selectedFilters");
        } else {
          setSelectedTab("filters");
        }
      }
    },
    []
  );

  return (
    <AiFilterContext.Provider
      value={{
        selectedItems,
        isSelected,
        selectItem,
        selectItems,
        toggleAll,
        clearSelectedAiFilter,
        pageSizeState,
        setPageSizeState,
        manageSelectedTab,
        selectedTab,
        setSelectedTab,
      }}
    >
      {children}
    </AiFilterContext.Provider>
  );
}